import { logger } from '~/utils/logger';

export const getEncryptionKeyFromString = async (encryptionSeed: string) => {
  if (!encryptionSeed || !crypto.subtle) {
    return null;
  }

  const encoder = new TextEncoder();
  const keyMaterial = encoder.encode(encryptionSeed);
  const hashBuffer = await crypto.subtle.digest('SHA-256', keyMaterial);

  // Import the hash directly as an AES-256 key
  const key = await crypto.subtle.importKey(
    'raw',
    hashBuffer,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['encrypt', 'decrypt'],
  );

  return key;
};

export async function encrypt(plaintext: string, key: CryptoKey | null) {
  if (!key) {
    return null;
  }

  try {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(plaintext);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedData = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      dataBuffer,
    );

    // Combine IV and encrypted data, then encode as base64
    const combined = new Uint8Array(iv.length + encryptedData.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encryptedData), iv.length);

    // Convert to base64 for storage
    // Use a more reliable method for base64 encoding
    const base64 = btoa(
      Array.from(combined)
        .map((byte) => String.fromCharCode(byte))
        .join(''),
    );

    return base64;
  } catch (error) {
    logger.encryption('Encryption error:', error);
    return null;
  }
}

/**
 * Decrypts data using AES-GCM with the encryption key
 */
export async function decrypt(encryptedData: string, key: CryptoKey | null) {
  if (!key) {
    return null;
  }

  try {
    const binaryString = atob(encryptedData);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const iv = bytes.slice(0, 12);
    const ciphertext = bytes.slice(12);
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      ciphertext,
    );

    const decoder = new TextDecoder();
    const plaintext = decoder.decode(decryptedData);
    return plaintext;
  } catch (error) {
    logger.encryption('Decryption error:', error);
    return null;
  }
}
